import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { drawKeyedWindow } from '@/lib/key-window'
import { gsap, prefersReducedMotion } from '@/lib/gsap'
import { useIntro } from '@/components/landing/intro-context'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import SplitChars from '@/components/landing/split-chars'
import { HERO_COPY, STATEMENT } from '@/constants/aerive'

const WINDOW_CLEAR = '/images/aerive/window-clear.png'
const WINDOW_FALLBACK = '/images/aerive/window.png'

const HeroWindow: FC = (): ReactNode => {
    const { introStarted, markWindowReady } = useIntro()
    const { scrollTo } = useSmoothScroll()
    const [fallback, setFallback] = useState(false)

    const sectionRef = useRef<HTMLElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const zoomRef = useRef<HTMLDivElement>(null)
    const tiltRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const cueRef = useRef<HTMLDivElement>(null)
    const cueInnerRef = useRef<HTMLDivElement>(null)
    const asideRef = useRef<HTMLDivElement>(null)
    const statementRef = useRef<HTMLDivElement>(null)

    // Cut the glass out of the window artwork so the live sky shows through it
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        let cancelled = false

        const small = window.innerWidth < 768
        const maxWidth = small ? 2048 : 2752
        // Ask the image optimiser for a lighter copy first, and fall back to the original file
        const optimised = `/_next/image?url=${encodeURIComponent(WINDOW_CLEAR)}&w=${small ? 2048 : 3840}&q=90`

        drawKeyedWindow(optimised, canvas, maxWidth)
            .catch(() => drawKeyedWindow(WINDOW_CLEAR, canvas, maxWidth))
            .catch((error: unknown) => {
                console.error(error)
                if (!cancelled) setFallback(true)
            })
            .finally(() => {
                if (!cancelled) markWindowReady()
            })

        return () => {
            cancelled = true
        }
    }, [markWindowReady])

    // The fly-through: pinned for two and a half screens of scroll, fully reversible
    useEffect(() => {
        const section = sectionRef.current
        const stage = stageRef.current
        if (!section || !stage) return

        const wordmark = document.querySelector<HTMLElement>('[data-flying-wordmark]')
        const skyZoom = document.querySelector<HTMLElement>('[data-sky="zoom"]')
        const sweep = document.querySelector<HTMLElement>('[data-sky="sweep"]')

        const headerCentre = (): number => (window.innerWidth < 768 ? 32 : 40)
        const wordmarkScale = (): number => {
            if (!wordmark) return 1
            const size = parseFloat(window.getComputedStyle(wordmark).fontSize) || 40
            return (window.innerWidth < 768 ? 19 : 22) / size
        }

        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)'
        }, (context) => {
            const { motion } = context.conditions as { motion: boolean }

            if (motion) {
                // Letters wait behind the preloader for the intro
                gsap.set(section.querySelectorAll('[data-char]'), { autoAlpha: 0, yPercent: 35, filter: 'blur(14px)' })
                gsap.set([asideRef.current, cueInnerRef.current], { autoAlpha: 0, y: 24 })
            } else if (wordmark) {
                gsap.set(wordmark, { y: headerCentre() - window.innerHeight / 2, scale: wordmarkScale() })
            }

            const timeline = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: () => `+=${Math.round(window.innerHeight * 2.5)}`,
                    scrub: 1,
                    pin: stage,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    refreshPriority: 10
                }
            })

            if (motion) {
                timeline
                    .to(zoomRef.current, { scale: 5.4, ease: 'power2.in', duration: 6.2 }, 0)
                    .to(zoomRef.current, { autoAlpha: 0, duration: 0.6 }, 5.8)
                    .to(skyZoom, { scale: 1.15, duration: 7 }, 0)
                    .to(copyRef.current, { scale: 2.4, autoAlpha: 0, ease: 'power2.in', duration: 3.4 }, 0)
                    .to(cueRef.current, { autoAlpha: 0, duration: 0.7 }, 0)
                    .fromTo(sweep, { xPercent: 30, autoAlpha: 0 }, { xPercent: -10, autoAlpha: 0.95, ease: 'power1.in', duration: 2.6 }, 2.6)
                    .to(sweep, { xPercent: -45, autoAlpha: 0, ease: 'power1.out', duration: 2 }, 5.2)
                    .fromTo(
                        statementRef.current,
                        { y: 150, scale: 0.85, autoAlpha: 0, filter: 'blur(10px)' },
                        { y: 0, scale: 1, autoAlpha: 1, filter: 'blur(0px)', ease: 'power3.out', duration: 3.2 },
                        6.1
                    )
                    .to({}, { duration: 0.7 })

                if (wordmark) {
                    timeline.to(wordmark, {
                        y: () => headerCentre() - window.innerHeight / 2,
                        scale: wordmarkScale,
                        ease: 'power2.inOut',
                        duration: 4.8
                    }, 0.9)
                }
            } else {
                timeline
                    .to(copyRef.current, { autoAlpha: 0, duration: 2 }, 0)
                    .to(cueRef.current, { autoAlpha: 0, duration: 0.6 }, 0)
                    .to(zoomRef.current, { autoAlpha: 0, duration: 2 }, 2.4)
                    .fromTo(statementRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 2 }, 4.2)
                    .to({}, { duration: 1 })
            }

            return () => {
                if (wordmark) gsap.set(wordmark, { clearProps: 'transform' })
            }
        })

        return () => mm.revert()
    }, [])

    // The window and the sky drift gently against each other under the cursor
    useEffect(() => {
        const tilt = tiltRef.current
        const parallax = document.querySelector<HTMLElement>('[data-sky="parallax"]')
        if (!tilt || prefersReducedMotion()) return
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

        gsap.set(tilt, { transformPerspective: 1400 })

        const rotateY = gsap.quickTo(tilt, 'rotationY', { duration: 1.4, ease: 'power3.out' })
        const rotateX = gsap.quickTo(tilt, 'rotationX', { duration: 1.4, ease: 'power3.out' })
        const shiftX = gsap.quickTo(tilt, 'x', { duration: 1.4, ease: 'power3.out' })
        const shiftY = gsap.quickTo(tilt, 'y', { duration: 1.4, ease: 'power3.out' })
        const skyX = parallax ? gsap.quickTo(parallax, 'x', { duration: 2, ease: 'power3.out' }) : null
        const skyY = parallax ? gsap.quickTo(parallax, 'y', { duration: 2, ease: 'power3.out' }) : null

        const handleMove = (event: PointerEvent): void => {
            if (window.scrollY > window.innerHeight * 2.6) return
            const x = event.clientX / window.innerWidth - 0.5
            const y = event.clientY / window.innerHeight - 0.5
            rotateY(x * 3.2)
            rotateX(-y * 2.4)
            shiftX(x * 14)
            shiftY(y * 10)
            skyX?.(-x * 26)
            skyY?.(-y * 18)
        }

        window.addEventListener('pointermove', handleMove, { passive: true })

        return () => {
            window.removeEventListener('pointermove', handleMove)
            gsap.killTweensOf(tilt)
            if (parallax) gsap.killTweensOf(parallax)
        }
    }, [])

    // Headlines resolve out of blur once the preloader lifts
    useEffect(() => {
        if (!introStarted || prefersReducedMotion()) return
        const section = sectionRef.current
        if (!section) return

        const timeline = gsap.timeline({ delay: 0.25 })

        timeline
            .to(section.querySelectorAll('[data-hero-lead] [data-char]'), {
                autoAlpha: 1,
                yPercent: 0,
                filter: 'blur(0px)',
                duration: 1.5,
                ease: 'power3.out',
                stagger: 0.045
            }, 0)
            .to(section.querySelectorAll('[data-hero-counter] [data-char]'), {
                autoAlpha: 1,
                yPercent: 0,
                filter: 'blur(0px)',
                duration: 1.5,
                ease: 'power3.out',
                stagger: 0.045
            }, 0.45)
            .to([asideRef.current, cueInnerRef.current], {
                autoAlpha: 1,
                y: 0,
                duration: 1.2,
                ease: 'power3.out',
                stagger: 0.15
            }, 0.9)

        return () => {
            timeline.kill()
        }
    }, [introStarted])

    return (
        <section id='hero' ref={sectionRef} aria-label='AERIVE private aviation' className='relative z-10'>
            <div ref={stageRef} className='stage relative w-full overflow-hidden'>
                <div ref={zoomRef} className='window-box will-change-transform'>
                    <div ref={tiltRef} className='absolute inset-0'>
                        <div className='window-band window-band-top' />
                        <div className='window-band window-band-bottom' />
                        <div className='window-band window-band-left' />
                        <div className='window-band window-band-right' />
                        <canvas
                            ref={canvasRef}
                            aria-hidden='true'
                            className='absolute inset-0 h-full w-full'
                        />
                        {fallback && (
                            <img
                                src={WINDOW_FALLBACK}
                                alt=''
                                className='absolute inset-0 h-full w-full object-cover'
                            />
                        )}
                    </div>
                </div>

                <div className='stage-safe absolute inset-x-0 top-0'>
                    <div ref={copyRef} className='absolute inset-0 will-change-transform'>
                        <h1
                            data-hero-lead
                            className='absolute left-5 top-[15%] font-heading text-[clamp(3.1rem,7.6vw,8.8rem)] font-normal leading-[0.88] tracking-[-0.01em] text-ivory md:left-[6vw] md:top-[15%]'
                        >
                            <span className='sr-only'>{HERO_COPY.lead.join(' ')}</span>
                            <SplitChars text={HERO_COPY.lead[0]} className='block' />
                            <SplitChars text={HERO_COPY.lead[1]} className='block pl-[0.55em]' />
                        </h1>

                        <p
                            data-hero-counter
                            className='absolute bottom-[21%] right-5 text-right font-heading text-[clamp(3.1rem,7.6vw,8.8rem)] font-normal leading-[0.88] tracking-[-0.01em] text-ivory md:bottom-[16%] md:right-[6vw]'
                        >
                            <span className='sr-only'>{HERO_COPY.counter.join(' ')}</span>
                            <SplitChars text={HERO_COPY.counter[0]} className='block pr-[0.55em]' />
                            <SplitChars text={HERO_COPY.counter[1]} className='block' />
                        </p>

                        <div ref={asideRef} className='absolute bottom-[9%] left-[6vw] hidden max-w-[19rem] md:block'>
                            <p className='text-[17px] font-medium leading-[1.25] text-ivory'>
                                {HERO_COPY.asideTitle[0]}
                                <br />
                                {HERO_COPY.asideTitle[1]}
                            </p>
                            <span className='my-5 block h-px w-8 bg-champagne' />
                            <p className='text-[13px] leading-relaxed text-ivory/75'>
                                {HERO_COPY.asideText}
                            </p>
                        </div>
                    </div>

                    <div ref={cueRef} className='absolute inset-x-5 bottom-24 md:bottom-[9%] md:left-auto md:right-[6vw] md:w-[26vw]'>
                        <div ref={cueInnerRef}>
                            <span className='mb-4 block h-px w-full bg-champagne/40' />
                            <button
                                type='button'
                                onClick={() => scrollTo('#about')}
                                className='flex w-full items-center justify-between text-[10px] uppercase tracking-[0.24em] text-ivory/85 transition-colors hover:text-champagne focus-visible:outline-none focus-visible:text-champagne'
                            >
                                <span className='flex items-center gap-3'>
                                    <span className='flex flex-col -space-y-2.5 text-champagne'>
                                        <ChevronDown className='scroll-cue-chevron size-4' />
                                        <ChevronDown className='scroll-cue-chevron size-4' />
                                    </span>
                                    {HERO_COPY.cueLeft}
                                </span>
                                <span className='hidden sm:inline'>{HERO_COPY.cueRight}</span>
                            </button>
                        </div>
                    </div>

                    <div ref={statementRef} className='invisible absolute inset-0 flex items-center justify-center px-6 opacity-0 md:px-[8vw]'>
                        <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(8,8,10,0.62)_0%,rgba(8,8,10,0.28)_45%,rgba(8,8,10,0)_72%)]' />
                        <p className='relative max-w-[60rem] text-center font-heading text-[clamp(1.9rem,3.7vw,3.7rem)] font-light leading-[1.14] text-ivory [text-shadow:0_2px_24px_rgba(0,0,0,0.35)]'>
                            <span className='pr-[0.2em] font-medium tracking-[0.2em] text-champagne'>{STATEMENT.brand}</span>
                            {' '}
                            {STATEMENT.text}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HeroWindow
